import type {
  AgentAvailability,
  AgentConversation,
  AgentProfile,
  CreateSessionRequest,
  LoginResponse,
  ProfileDraft,
  PromptDraft,
  PromptTemplate,
  SendPromptRequest,
  SendPromptResult,
  SessionInfo,
  SetAutonomyResult,
  UserInfo,
  Workspace,
  WorkspaceDraft,
} from '@cm/protocol';

/**
 * Host ativo, definido pela store.
 *
 * Guardado aqui num modulo em vez de lido da store para evitar dependencia
 * circular (api -> store -> api). A store e a fonte da verdade e empurra o
 * host para ca sempre que ele muda.
 */
let activeHost: { url: string; token: string | null } | null = null;

export function setActiveHostForApi(host: { url: string; token: string | null } | null): void {
  activeHost = host;
}

/**
 * Endereco embutido em tempo de build (`VITE_CM_DAEMON_URL`).
 *
 * E o que faz o app desktop funcionar sem configuracao: ele ja nasce sabendo
 * onde esta o daemon. Fica vazio na build web, que e servida pelo proprio
 * daemon e portanto usa a mesma origem.
 */
const BUILT_IN_DAEMON_URL = (import.meta.env.VITE_CM_DAEMON_URL ?? '')
  .trim()
  .replace(/\/+$/, '');

export function getDaemonUrl(): string {
  // O host ativo vence o default do build; sem isso, nao haveria como apontar
  // o app para outro daemon depois de instalado.
  return activeHost ? activeHost.url : BUILT_IN_DAEMON_URL;
}

/** Endereco de fabrica, usado para semear o primeiro host. */
export function builtInDaemonUrl(): string {
  return BUILT_IN_DAEMON_URL;
}

/** Se o app ja vem com um daemon configurado de fabrica. */
export function hasBuiltInDaemon(): boolean {
  return BUILT_IN_DAEMON_URL !== '';
}

export function getToken(): string | null {
  return activeHost?.token ?? null;
}

/** URL do WebSocket derivada da base HTTP, com o token na query. */
export function websocketUrl(token: string): string {
  const base = getDaemonUrl();
  const httpBase = base || window.location.origin;
  const wsBase = httpBase.replace(/^http/, 'ws');
  return `${wsBase}/ws?token=${encodeURIComponent(token)}`;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  headers.set('content-type', 'application/json');
  if (token) headers.set('authorization', `Bearer ${token}`);

  const res = await fetch(`${getDaemonUrl()}${path}`, { ...init, headers });

  if (res.status === 204) return undefined as T;

  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = { error: text };
    }
  }

  if (!res.ok) {
    const err = body as { error?: string; detail?: string } | null;
    throw new ApiError(err?.error ?? `HTTP ${res.status}`, res.status, err?.detail);
  }
  return body as T;
}

export const api = {
  // --- auth ---
  login: (username: string, password: string) =>
    request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  me: () => request<{ user: UserInfo }>('/api/auth/me'),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: boolean }>('/api/auth/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  // --- agentes ---
  agents: () => request<{ agents: AgentAvailability[] }>('/api/agents'),

  /**
   * Conversas gravadas pelo CLI. O `cwd` filtra pelo projeto -- sem ele a
   * lista traz conversas de qualquer pasta.
   */
  conversations: (agent: string, cwd?: string) =>
    request<{ conversations: AgentConversation[] }>(
      `/api/conversations?agent=${encodeURIComponent(agent)}` +
        (cwd ? `&cwd=${encodeURIComponent(cwd)}` : ''),
    ),

  // --- workspaces ---
  workspaces: () => request<{ workspaces: Workspace[] }>('/api/workspaces'),

  createWorkspace: (draft: WorkspaceDraft) =>
    request<{ workspace: Workspace }>('/api/workspaces', {
      method: 'POST',
      body: JSON.stringify(draft),
    }),

  updateWorkspace: (id: string, patch: Partial<WorkspaceDraft>) =>
    request<{ workspace: Workspace }>(`/api/workspaces/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteWorkspace: (id: string) =>
    request<{ ok: boolean }>(`/api/workspaces/${id}`, { method: 'DELETE' }),

  // --- sessoes ---
  sessions: () => request<{ sessions: SessionInfo[] }>('/api/sessions'),

  createSession: (body: CreateSessionRequest) =>
    request<{ session: SessionInfo; warnings: string[] }>('/api/sessions', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  killSession: (id: string) =>
    request<{ session: SessionInfo }>(`/api/sessions/${id}`, { method: 'DELETE' }),

  setAutonomy: (id: string, autonomy: string, allowRestart: boolean) =>
    request<SetAutonomyResult>(`/api/sessions/${id}/autonomy`, {
      method: 'POST',
      body: JSON.stringify({ autonomy, allowRestart }),
    }),

  restartSession: (id: string, body: { autonomy?: string; model?: string | null; resume?: boolean }) =>
    request<{ session: SessionInfo }>(`/api/sessions/${id}/restart`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  cycleAutonomy: (id: string) =>
    request<{ ok: boolean; reason?: string }>(`/api/sessions/${id}/cycle-autonomy`, {
      method: 'POST',
    }),

  sendKey: (id: string, key: string) =>
    request<{ ok: boolean }>(`/api/sessions/${id}/keys`, {
      method: 'POST',
      body: JSON.stringify({ key }),
    }),

  // --- perfis ---
  profiles: () => request<{ profiles: AgentProfile[] }>('/api/profiles'),

  createProfile: (draft: ProfileDraft) =>
    request<{ profile: AgentProfile; argv?: string[]; warnings?: string[] }>('/api/profiles', {
      method: 'POST',
      body: JSON.stringify(draft),
    }),

  updateProfile: (id: string, patch: Partial<ProfileDraft>) =>
    request<{ profile: AgentProfile; argv?: string[]; warnings?: string[] }>(
      `/api/profiles/${id}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    ),

  deleteProfile: (id: string) =>
    request<{ ok: boolean }>(`/api/profiles/${id}`, { method: 'DELETE' }),

  previewProfile: (id: string) =>
    request<{ argv: string[]; warnings: string[] }>(`/api/profiles/${id}/preview`),

  // --- prompts ---
  prompts: () => request<{ prompts: PromptTemplate[] }>('/api/prompts'),

  createPrompt: (draft: PromptDraft) =>
    request<{ prompt: PromptTemplate }>('/api/prompts', {
      method: 'POST',
      body: JSON.stringify(draft),
    }),

  updatePrompt: (id: string, patch: Partial<PromptDraft>) =>
    request<{ prompt: PromptTemplate }>(`/api/prompts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deletePrompt: (id: string) =>
    request<{ ok: boolean }>(`/api/prompts/${id}`, { method: 'DELETE' }),

  sendPrompt: (body: SendPromptRequest) =>
    request<SendPromptResult>('/api/prompts/send', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};
